/*
Linze Form Component for Vehicle Publication
Complete editable form with draft save/load and edit mode
Autor: AI Assistant
Fecha: 2025-11-25
*/
import React, { useEffect, useState } from 'react'
import axios from 'axios'

import Vehicle from '../interfaces/Vehicle'
import { useAuth } from '../hooks/useAuth'
import { BranchDto } from '../interfaces/Branch'
import { getToken } from '../utils/utils'
import {
  makeUrlLinzeDraft,
  makeUrlLinzePublish,
  makeUrlLinzeUpdate,
  makeUrlLinzeSold,
  makeUrlLinzeDelete,
} from '../hooks/connectionData'

import ConfirmationModal from './ConfirmationModal'
import DisplayError from './DisplayError'
import { Info } from './MessageInfo'
import { ButtonAction } from './Buttons'

interface LinzeFormProps {
  vehicle: Vehicle
  branch: BranchDto | null
  onPublishSuccess: () => void
}

// Constants
const CHILEAN_REGIONS = [
  'Región de Arica y Parinacota',
  'Región de Tarapacá',
  'Región de Antofagasta',
  'Región de Atacama',
  'Región de Coquimbo',
  'Región de Valparaíso',
  'Región Metropolitana de Santiago',
  "Región del Libertador Bernardo O'Higgins",
  'Región del Maule',
  'Región de Ñuble',
  'Región del Biobío',
  'Región de La Araucanía',
  'Región de Los Ríos',
  'Región de Los Lagos',
  'Región de Aysén',
  'Región de Magallanes',
]

const FUEL_TYPES = ['Bencina', 'Diesel', 'Hibrido', 'Electrico']

const BODY_TYPES = [
  'Sedan',
  'Hatchback',
  'SUV',
  'Coupé',
  'Cabina Simple',
  'Cabina Doble',
  'Station Wagon',
  'Furgón',
  'Camión',
  'Acoplado',
  'Otro',
]

const TRANSMISSION_TYPES = ['Mecanica', 'Automatico']

// COLORS commented out - not needed since color field is removed
// const COLORS = [
//   "Blanco", "Negro", "Gris", "Plata", "Rojo",
//   "Azul", "Verde", "Amarillo", "Naranja", "Café",
//   "Beige", "Dorado", "Otro"
// ]

interface LinzeFormData {
  comments: string
  region: string
  accidents_reported: boolean
  good_maintenance: boolean
  personal_use: boolean
  sole_owner: boolean
  negotiate: boolean
  version: string
  color: string
  fuel: string
  type: string
  transmission: string
  doors: number
}

const LinzeForm: React.FC<LinzeFormProps> = ({ vehicle, branch, onPublishSuccess }) => {
  const { userData } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [confirmAction, setConfirmAction] = useState<'publish' | 'delete' | null>(null)

  // Notification system
  const [publishMessage, setPublishMessage] = useState<string>('')
  const [publishTrigger, setPublishTrigger] = useState<number>(0)

  // Check if vehicle is already published on Linze
  const isPublished = !!vehicle.external_linze?.trackingId

  // State management
  const [faltaMsg, setFaltaMsg] = useState<string>('')
  const [disabled, setDisabled] = useState<boolean>(false)

  // Form data state
  const [formData, setFormData] = useState<LinzeFormData>({
    comments: '',
    region: '',
    accidents_reported: false,
    good_maintenance: true,
    personal_use: true,
    sole_owner: true,
    negotiate: true,
    version: '',
    color: '',
    fuel: 'Bencina',
    type: 'Sedan',
    transmission: 'Mecanica',
    doors: 4,
  })

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [originalData, setOriginalData] = useState<LinzeFormData | null>(null)
  const [isDirty, setIsDirty] = useState<boolean>(false)

  useEffect(() => {
    let isEnabled = true

    if (!userData?.organization) {
      setFaltaMsg('Usuario no autenticado.')
      isEnabled = false
    } else if (!vehicle.branch) {
      setFaltaMsg('Asigna una sucursal al vehículo.')
      isEnabled = false
    }

    if (!isEnabled) {
      setDisabled(true)
    }
  }, [userData, vehicle, branch])

  // Load draft or published data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!vehicle._id) return

      try {
        const token = getToken()
        const response = await axios.get(makeUrlLinzeDraft(vehicle._id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.data.success && response.data.data) {
          const draft = response.data.data
          // Merge draft data with vehicle data
          setFormData({
            comments: draft.comments || vehicle.comments || '',
            region: draft.region || 'Región Metropolitana de Santiago',
            accidents_reported: draft.accidents_reported ?? false,
            good_maintenance: draft.good_maintenance ?? true,
            personal_use: draft.personal_use ?? true,
            sole_owner: draft.sole_owner ?? (vehicle.owners === 1 ? true : false),
            negotiate: draft.negotiate ?? true,
            version: draft.version || vehicle.version || '',
            color: draft.color || vehicle.color || '',
            fuel: draft.fuel || mapFuelFromVehicle(vehicle.fuel_format) || 'Bencina',
            type: draft.type || mapTypeFromVehicle(vehicle.body_format) || 'Sedan',
            transmission: draft.transmission || mapTransmissionFromVehicle(vehicle.gearbox_format) || 'Mecanica',
            doors: draft.doors || 4,
          })
        } else {
          // No draft, use vehicle defaults
          setFormData({
            comments: vehicle.comments || '',
            region: 'Región Metropolitana de Santiago',
            accidents_reported: false,
            good_maintenance: true,
            personal_use: true,
            sole_owner: vehicle.owners === 1,
            negotiate: true,
            version: vehicle.version || '',
            color: vehicle.color || '',
            fuel: mapFuelFromVehicle(vehicle.fuel_format) || 'Bencina',
            type: mapTypeFromVehicle(vehicle.body_format) || 'Sedan',
            transmission: mapTransmissionFromVehicle(vehicle.gearbox_format) || 'Mecanica',
            doors: 4,
          })
        }
      } catch (error) {
        console.error('Error loading draft:', error)
        // Use vehicle defaults on error
        setFormData({
          comments: vehicle.comments || '',
          region: 'Región Metropolitana de Santiago',
          accidents_reported: false,
          good_maintenance: true,
          personal_use: true,
          sole_owner: vehicle.owners === 1,
          negotiate: true,
          version: vehicle.version || '',
          color: vehicle.color || '',
          fuel: mapFuelFromVehicle(vehicle.fuel_format) || 'Bencina',
          type: mapTypeFromVehicle(vehicle.body_format) || 'Sedan',
          transmission: mapTransmissionFromVehicle(vehicle.gearbox_format) || 'Mecanica',
          doors: 4,
        })
      }
    }

    loadData()
  }, [vehicle._id])

  // Helper functions to map vehicle data to Linze format
  const mapFuelFromVehicle = (fuel: string | undefined): string => {
    if (!fuel) return 'Bencina'
    const fuelLower = fuel.toLowerCase()

    if (fuelLower.includes('diésel') || fuelLower.includes('diesel')) return 'Diesel'

    if (fuelLower.includes('híbrido') || fuelLower.includes('hibrido')) return 'Hibrido'

    if (fuelLower.includes('eléctrico') || fuelLower.includes('electrico')) return 'Electrico'

    return 'Bencina'
  }

  const mapTypeFromVehicle = (type: string | undefined): string => {
    if (!type) return 'Sedan'
    const typeLower = type.toLowerCase()

    if (typeLower.includes('sedan')) return 'Sedan'

    if (typeLower.includes('hatchback')) return 'Hatchback'

    if (typeLower.includes('suv')) return 'SUV'

    if (typeLower.includes('coupé') || typeLower.includes('coupe')) return 'Coupé'

    if (typeLower.includes('cabina simple')) return 'Cabina Simple'

    if (typeLower.includes('cabina doble')) return 'Cabina Doble'

    if (typeLower.includes('station') || typeLower.includes('familiar')) return 'Station Wagon'

    if (typeLower.includes('furgón') || typeLower.includes('furgon')) return 'Furgón'

    if (typeLower.includes('camión') || typeLower.includes('camion')) return 'Camión'

    if (typeLower.includes('acoplado')) return 'Acoplado'

    return 'Otro'
  }

  const mapTransmissionFromVehicle = (transmission: string | undefined): string => {
    if (!transmission) return 'Mecanica'
    const transLower = transmission.toLowerCase()

    if (transLower.includes('automát') || transLower.includes('auto')) return 'Automatico'

    return 'Mecanica'
  }

  // Handle field changes
  const handleFieldChange = (field: keyof LinzeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setIsDirty(true)
  }

  // Save draft
  const saveDraft = async () => {
    try {
      setIsLoading(true)
      setError('')

      const token = getToken()
      await axios.post(makeUrlLinzeDraft(vehicle._id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      setPublishMessage('✅ Borrador guardado exitosamente')
      setPublishTrigger(Date.now())
      setIsDirty(false)
    } catch (error: any) {
      console.error('Error saving draft:', error)
      const errorMessage = error.response?.data?.message || error.message
      setError('Error al guardar borrador: ' + errorMessage)
      setPublishMessage(`❌ Error al guardar: ${errorMessage}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsLoading(false)
    }
  }

  // Validation helper
  const validateBeforePublish = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    // Check vehicle has images
    if (!vehicle.pictures?.public || vehicle.pictures.public.length === 0) {
      errors.push('El vehículo debe tener al menos una imagen pública')
    }

    // Check required fields from vehicle
    if (!vehicle.brand) errors.push('Marca es requerida')

    if (!vehicle.model) errors.push('Modelo es requerido')

    if (!vehicle.year) errors.push('Año es requerido')

    if (!vehicle.plate) errors.push('Patente es requerida')

    if (!vehicle.price) errors.push('Precio es requerido')

    if (!vehicle.odometer) errors.push('Kilometraje es requerido')

    // Check required form fields
    if (!formData.region) errors.push('Región es requerida')

    if (!formData.color) errors.push('Color es requerido')

    if (!formData.fuel) errors.push('Combustible es requerido')

    if (!formData.type) errors.push('Tipo de carrocería es requerido')

    if (!formData.transmission) errors.push('Transmisión es requerida')

    // VIN validation: If provided, must be exactly 17 characters (Linze requirement)
    if (vehicle.chassisId && vehicle.chassisId.length !== 17) {
      errors.push(
        `El VIN debe tener exactamente 17 caracteres (actual: ${vehicle.chassisId.length}). Déjalo vacío si no lo conoces.`
      )
    }

    return { valid: errors.length === 0, errors }
  }

  // Publishing functions
  const publishToLinze = async () => {
    try {
      setIsLoading(true)
      setError('')

      // Validate before publishing
      const validation = validateBeforePublish()

      if (!validation.valid) {
        setError(validation.errors.join(', '))
        setPublishMessage(`❌ Validación fallida: ${validation.errors.join(', ')}`)
        setPublishTrigger(Date.now())
        setIsLoading(false)

        return
      }

      // Save draft first
      await saveDraft()

      const token = getToken()
      const response = await axios.post(
        makeUrlLinzePublish(vehicle._id),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data) {
        setPublishMessage('✅ Vehículo publicado exitosamente en Linze')
        setPublishTrigger(Date.now())
        setIsDirty(false)
        onPublishSuccess()
      }
    } catch (err: any) {
      console.error('Linze publish error:', err)
      let errorMessage = 'Error al publicar en Linze'

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setPublishMessage(`❌ ${errorMessage}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsLoading(false)
    }
  }

  const updateInLinze = async () => {
    try {
      setIsLoading(true)
      setError('')

      // Save draft first
      await saveDraft()

      const token = getToken()
      const response = await axios.patch(
        makeUrlLinzeUpdate(vehicle._id),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data) {
        setPublishMessage('✅ Vehículo actualizado exitosamente en Linze')
        setPublishTrigger(Date.now())
        setIsEditMode(false)
        setIsDirty(false)
        onPublishSuccess()
      }
    } catch (err: any) {
      console.error('Linze update error:', err)
      let errorMessage = 'Error al actualizar en Linze'

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setPublishMessage(`❌ ${errorMessage}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsLoading(false)
    }
  }

  const deleteFromLinze = async () => {
    try {
      setIsLoading(true)
      setError('')

      const token = getToken()
      const response = await axios.delete(makeUrlLinzeDelete(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { vehicleId: vehicle._id },
      })

      if (response.data) {
        setPublishMessage('✅ Vehículo eliminado exitosamente de Linze')
        setPublishTrigger(Date.now())
        onPublishSuccess()
      }
    } catch (err: any) {
      console.error('Linze delete error:', err)
      let errorMessage = 'Error al eliminar de Linze'

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setPublishMessage(`❌ ${errorMessage}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsLoading(false)
    }
  }

  const markAsSoldInLinze = async () => {
    try {
      setIsLoading(true)
      setError('')

      const token = getToken()
      const response = await axios.patch(
        makeUrlLinzeSold(vehicle._id),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data) {
        setPublishMessage('✅ Vehículo marcado como vendido en Linze')
        setPublishTrigger(Date.now())
        onPublishSuccess()
      }
    } catch (err: any) {
      console.error('Linze sold error:', err)
      let errorMessage = 'Error al marcar como vendido en Linze'

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      setPublishMessage(`❌ ${errorMessage}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsLoading(false)
    }
  }

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setIsEditMode(true)
    setOriginalData({ ...formData })
  }

  const handleCancelEdit = () => {
    if (originalData) {
      setFormData(originalData)
    }
    setIsEditMode(false)
    setIsDirty(false)
    setOriginalData(null)
  }

  const handleConfirmEdit = async () => {
    await updateInLinze()
  }

  // Event handlers
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    if (confirmAction === 'publish') {
      await publishToLinze()
    } else if (confirmAction === 'delete') {
      await deleteFromLinze()
    }
    setConfirmAction(null)
  }

  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
    setConfirmAction(null)
  }

  const handlePublish = () => {
    setConfirmAction('publish')
    setConfirmationModalOpen(true)
  }

  const handleDeleteClick = () => {
    setConfirmAction('delete')
    setConfirmationModalOpen(true)
  }

  const canEdit = !isPublished || isEditMode

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Publicar en Linze</h3>

      {/* Error/Fault Message Display */}
      {faltaMsg && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-700 text-sm">{faltaMsg}</div>
        </div>
      )}

      {/* Published Status */}
      {isPublished && !isEditMode && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="text-green-700 text-sm">
            ✅ Publicado en Linze
            {vehicle.external_linze?.trackingLink && (
              <div className="mt-2">
                <a
                  href={vehicle.external_linze.trackingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Ver publicación en Linze
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vehicle Information Display (Read-Only) */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h4 className="font-medium text-gray-700 mb-3">Información del Vehículo</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <strong>Marca:</strong> {vehicle.brand}
          </div>
          <div>
            <strong>Modelo:</strong> {vehicle.model}
          </div>
          <div>
            <strong>Año:</strong> {vehicle.year}
          </div>
          <div>
            <strong>Patente:</strong> {vehicle.plate}
          </div>
          <div>
            <strong>Precio:</strong> {vehicle.price_format}
          </div>
          <div>
            <strong>Kilometraje:</strong> {vehicle.odometer_format}
          </div>
          {vehicle.chassisId && (
            <div>
              <strong>VIN:</strong> {vehicle.chassisId}
            </div>
          )}
          {vehicle.engineId && (
            <div>
              <strong>Motor:</strong> {vehicle.engineId}
            </div>
          )}
        </div>
      </div>

      {/* Editable Form Fields */}
      <div className="space-y-6">
        {/* COMMENTED OUT - Description
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción del Vehículo
          </label>
          <textarea
            value={formData.comments}
            onChange={(e) => handleFieldChange('comments', e.target.value)}
            disabled={!canEdit}
            rows={4}
            className={`w-full border rounded-md px-3 py-2 text-sm ${
              canEdit ? 'bg-white' : 'bg-gray-100'
            }`}
            placeholder="Describe el vehículo (opcional)"
          />
          <div className="text-xs text-gray-500 mt-1">
            {formData.comments.length} caracteres
          </div>
        </div>
        */}

        {/* Location Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3 text-base">📍 Ubicación</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Región <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.region}
              onChange={(e) => handleFieldChange('region', e.target.value)}
              disabled={!canEdit}
              className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                canEdit ? 'bg-white' : 'bg-gray-100'
              }`}
            >
              <option value="">Selecciona una región</option>
              {CHILEAN_REGIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Technical Specifications Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3 text-base">🔧 Especificaciones Técnicas</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fuel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Combustible <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.fuel}
                onChange={(e) => handleFieldChange('fuel', e.target.value)}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
              >
                {FUEL_TYPES.map((fuel) => (
                  <option key={fuel} value={fuel}>
                    {fuel}
                  </option>
                ))}
              </select>
            </div>

            {/* Transmission */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transmisión <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.transmission}
                onChange={(e) => handleFieldChange('transmission', e.target.value)}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
              >
                {TRANSMISSION_TYPES.map((trans) => (
                  <option key={trans} value={trans}>
                    {trans}
                  </option>
                ))}
              </select>
            </div>

            {/* Body Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Carrocería <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleFieldChange('type', e.target.value)}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
              >
                {BODY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Doors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Número de Puertas</label>
              <input
                type="number"
                min="2"
                max="5"
                value={formData.doors}
                onChange={(e) => {
                  const val = e.target.value

                  if (val === '') {
                    handleFieldChange('doors', 4)
                  } else {
                    const parsed = parseInt(val)

                    if (!isNaN(parsed) && parsed >= 2 && parsed <= 5) {
                      handleFieldChange('doors', parsed)
                    }
                  }
                }}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
              />
            </div>

            {/* Version */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Versión</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => handleFieldChange('version', e.target.value)}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
                placeholder="Ej: XEi, GT, Limited"
              />
            </div>

            {/* COMMENTED OUT - Color
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.color}
                onChange={(e) => handleFieldChange('color', e.target.value)}
                disabled={!canEdit}
                className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  canEdit ? 'bg-white' : 'bg-gray-100'
                }`}
              >
                <option value="">Selecciona un color</option>
                {COLORS.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </div>
            */}
          </div>
        </div>

        {/* Vehicle History Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3 text-base">📋 Historial del Vehículo</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.accidents_reported}
                onChange={(e) => handleFieldChange('accidents_reported', e.target.checked)}
                disabled={!canEdit}
                className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={!canEdit ? 'text-gray-500' : 'text-gray-700'}>Reporta accidentes</span>
            </label>

            <label className="flex items-center text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.good_maintenance}
                onChange={(e) => handleFieldChange('good_maintenance', e.target.checked)}
                disabled={!canEdit}
                className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={!canEdit ? 'text-gray-500' : 'text-gray-700'}>Buen mantenimiento</span>
            </label>

            <label className="flex items-center text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.personal_use}
                onChange={(e) => handleFieldChange('personal_use', e.target.checked)}
                disabled={!canEdit}
                className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={!canEdit ? 'text-gray-500' : 'text-gray-700'}>Uso personal</span>
            </label>

            <label className="flex items-center text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.sole_owner}
                onChange={(e) => handleFieldChange('sole_owner', e.target.checked)}
                disabled={!canEdit}
                className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={!canEdit ? 'text-gray-500' : 'text-gray-700'}>Único dueño</span>
            </label>
          </div>
        </div>

        {/* Sale Details Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3 text-base">💰 Detalles de Venta</h4>
          <label className="flex items-center text-sm cursor-pointer hover:bg-gray-50 p-2 rounded">
            <input
              type="checkbox"
              checked={formData.negotiate}
              onChange={(e) => handleFieldChange('negotiate', e.target.checked)}
              disabled={!canEdit}
              className="mr-3 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className={!canEdit ? 'text-gray-500' : 'text-gray-700 font-medium'}>Precio negociable</span>
          </label>
        </div>
      </div>

      {/* Notification display */}
      {publishMessage && (
        <div className="flex justify-center mt-4">
          <Info
            message={publishMessage}
            trigger={publishTrigger}
            onTimeout={() => setPublishMessage('')}
            noCenter={true}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center mt-6 gap-3 flex-wrap">
        {!isPublished ? (
          // Not published - Show save draft and publish buttons
          <>
            <ButtonAction
              label={isLoading ? 'Guardando...' : 'Guardar Borrador'}
              handleButtonClick={saveDraft}
              disabled={!isDirty || isLoading || disabled}
            />
            <ButtonAction
              label={isLoading ? 'Publicando...' : 'Publicar en Linze'}
              handleButtonClick={handlePublish}
              disabled={disabled || isLoading}
            />
          </>
        ) : !isEditMode ? (
          // Published - View mode
          <>
            <ButtonAction label="Editar Publicación" handleButtonClick={handleEnterEditMode} disabled={disabled} />
            <ButtonAction
              label={isLoading ? 'Procesando...' : 'Marcar como Vendido'}
              handleButtonClick={markAsSoldInLinze}
              disabled={disabled || isLoading}
            />
            <ButtonAction
              label={isLoading ? 'Eliminando...' : 'Eliminar de Linze'}
              handleButtonClick={handleDeleteClick}
              disabled={disabled || isLoading}
            />
          </>
        ) : (
          // Published - Edit mode
          <>
            <ButtonAction
              label={isLoading ? 'Guardando...' : 'Guardar Cambios'}
              handleButtonClick={saveDraft}
              disabled={!isDirty || isLoading}
            />
            <ButtonAction
              label={isLoading ? 'Confirmando...' : 'Confirmar Cambios'}
              handleButtonClick={handleConfirmEdit}
              disabled={isLoading}
            />
            <ButtonAction label="Cancelar" handleButtonClick={handleCancelEdit} disabled={isLoading} />
          </>
        )}
      </div>

      {/* Error Display */}
      {error && <DisplayError msg={error} />}

      {/* Confirmation Modal */}
      {confirmationModalOpen && (
        <ConfirmationModal
          message={
            confirmAction === 'publish'
              ? '¿Está seguro de que desea publicar este vehículo en Linze?'
              : '¿Está seguro de que desea eliminar este vehículo de Linze?'
          }
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}
    </div>
  )
}

export default LinzeForm
